
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  LEGACY_MEMBER_LOGIN_URL,
  LEGACY_MEMBER_URL,
  LEGACY_STAFF_LOGIN_URL,
  LEGACY_STAFF_URL,
} from "../../Enums/LegacyRedirection";
import { useDispatch } from "react-redux";
import {
  resetGlobeLoader,
  showLoader,
} from "../../redux/loaderActions";
import { setScrollPosition } from "../../redux";
import AuthContext from "../../store/AuthContext";
import { useSelector } from "react-redux";
import Footer from "../../components/Layout/Footer/Footer";

interface ILegacyIFrame {
  isStaffUrl?: boolean
}

function updateUrlWithoutSensitiveParams(pathname: string, search: string) {
  const searchParams = new URLSearchParams(search);
  searchParams.delete('hide-navigation');
  searchParams.delete('token');
  searchParams.delete('test-environment');
  const newSearch = searchParams.toString();
  const newUrl = newSearch ? `${pathname}?${newSearch}` : pathname;
  window.history.replaceState({}, '', newUrl);
}

const environment = process.env.REACT_APP_ENVIRONMENT as 'dev' | 'testing' | 'uat' | 'prod';

/** Legacy iframe pages do not always post `pageLoaded` after `navigationStart`. */
const LEGACY_LOADER_MAX_WAIT_MS = 30000;

const LegacyIFrame: React.FC<ILegacyIFrame> = ({isStaffUrl}) => {
  const iframeRef = useRef(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const loaderReleaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const location = useLocation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [token, setToken] = useState<string | null>(null);
  const [cookieSet, setCookieSet] = useState<boolean>(false);
  const [iframeHeight, setIframeHeight] = useState<string>("100%");
  const { loggedPerson, isAuthenticated } = useContext(AuthContext);
  const reloadSignal = useSelector((state: any) => state.iframe?.reloadSignal);
  const navigate = useNavigate();

  const clearLoaderReleaseTimer = useCallback(() => {
    if (loaderReleaseTimerRef.current !== null) {
      clearTimeout(loaderReleaseTimerRef.current);
      loaderReleaseTimerRef.current = null;
    }
  }, []);

  const completeLegacyLoad = useCallback(() => {
    clearLoaderReleaseTimer();
    resetGlobeLoader();
  }, [clearLoaderReleaseTimer]);

  const beginLegacyLoad = useCallback(() => {
    clearLoaderReleaseTimer();
    showLoader();
    loaderReleaseTimerRef.current = setTimeout(() => {
      loaderReleaseTimerRef.current = null;
      resetGlobeLoader();
    }, LEGACY_LOADER_MAX_WAIT_MS);
  }, [clearLoaderReleaseTimer]);

  useEffect(() => {
    return () => {
      clearLoaderReleaseTimer();
      resetGlobeLoader();
    };
  }, [clearLoaderReleaseTimer]);


  useEffect(() => {
    const tokenCookie = document.cookie
      .split(";")
      .find((cookie) => cookie.trim().startsWith("token="))
      ?.trim().substring(6);
    if (tokenCookie) {
      setToken(tokenCookie);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isLandingPage = location.pathname === '/' || location.pathname === '/login.asp';
  // Always show the footer (revert logic)

  useEffect(() => {
    const calculateIframeHeight = () => {
      if (!isLandingPage) {
        const headerHeight = 90;
        const viewportHeight = window.innerHeight;
        const minIframeHeight = viewportHeight - headerHeight;
        setIframeHeight(`${Math.max(minIframeHeight, 600)}px`);
        return;
      }

      const findScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
        if (!element) {
          return null;
        }
        const parent = element.parentElement;
        if (!parent) {
          return null;
        }
        
        const style = window.getComputedStyle(parent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          return parent;
        }
        return findScrollableParent(parent);
      };

      const scrollableParent = findScrollableParent(containerRef.current);
      
      if (scrollableParent) {
        const parentHeight = scrollableParent.clientHeight;
        setIframeHeight(`${parentHeight}px`);
      } else {
        const headerHeight = 90;
        const viewportHeight = window.innerHeight;
        const iframeHeight = viewportHeight - headerHeight;
        setIframeHeight(`${Math.max(iframeHeight, 600)}px`);
      }
    };

    calculateIframeHeight();
    
    const timeoutId = setTimeout(() => {
      calculateIframeHeight();
    }, 200);
    
    window.addEventListener('resize', calculateIframeHeight);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateIframeHeight);
    };
  }, [location.pathname, isLandingPage]);

  useEffect(() => {
    if (cookieSet) {
      return;
    }
    if (!token) {
      setCookieSet(true);
      return;
    }

    const loginUrl = isStaffUrl ? LEGACY_STAFF_LOGIN_URL : LEGACY_MEMBER_LOGIN_URL;
    const seedIframe = document.createElement("iframe");
    seedIframe.style.display = "none";
    seedIframe.style.width = "0";
    seedIframe.style.height = "0";

    const url = new URL(loginUrl);
    url.searchParams.append("token", token);

    const markCookieSet = () => setCookieSet(true);
    seedIframe.onload = markCookieSet;
    seedIframe.onerror = markCookieSet;
    seedIframe.src = url.toString();
    document.body.appendChild(seedIframe);

    return () => {
      if (seedIframe.parentNode) {
        seedIframe.parentNode.removeChild(seedIframe);
      }
    };
  }, [token, isStaffUrl, cookieSet]);

  const handleIframeEvents = (event: MessageEvent) => {
    const { data } = event;
    if (!data || typeof data !== "object" || !data.type) {
      return;
    }
    
    if (data.type === "navigateFromLegacy") {
      const rawUrl: string = data.url || "";
      let url = rawUrl;
      try {
        const parsed = new URL(rawUrl, window.location.origin);
        parsed.searchParams.delete("token");
        parsed.searchParams.delete("hide-navigation");
        parsed.searchParams.delete("test-environment");
        url = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      } catch {
        url = rawUrl;
      }
      const isAuthPage = url.includes('/login') || url.includes('/two-factor-auth');
      if (isAuthPage) {
        navigate(url, { replace: true });
      } else {
        navigate(url);
      }
    }

    if (data.type === "scroll") {
      const { scrollPosition } = data.data;
      dispatch(
        setScrollPosition({
          pathname: location.pathname,
          scrollPosition: scrollPosition,
        })
      );
    } else if (data.type === "navigationStart") {
      resetGlobeLoader();
      beginLegacyLoad();
    } else if (data.type === "pageLoaded") {
      completeLegacyLoad();
    } else if (data.type === "logoff") {
      window.location.href = "/login";
    } else if (data.type === "login") {
      if (data.legacyUrl) {
        window.location.href = `/login?legacyUrl=${data.legacyUrl}`;
      }
      else {
        window.location.href = '/login'
      }
    } else if (data.type === "urlUpdated") {
      try {
        const { pathname, search } = JSON.parse(data.data.location);
        updateUrlWithoutSensitiveParams(pathname, search);
      } catch {
        /* ignore malformed legacy location payloads */
      }
      completeLegacyLoad();
    }
  };

  const loadIframe = () => {
    if (!iframeRef.current) {
      return;
    }
    resetGlobeLoader();
    beginLegacyLoad();
    const iframe = iframeRef.current as HTMLIFrameElement;
    const path = `${location.pathname}${location.search}`
    const legacyPath = `${isStaffUrl? LEGACY_STAFF_URL: LEGACY_MEMBER_URL }${path}`
      
    const url = new URL(legacyPath);
    url.searchParams.delete("hide-navigation");
    url.searchParams.delete("test-environment");
    const hideNavitation = Boolean(loggedPerson || isAuthenticated || token);

    url.searchParams.append("hide-navigation", hideNavitation.toString());
    if (token) {
      url.searchParams.append("token", token);
    }
    
    if (environment === 'testing' || environment === 'dev') {
      url.searchParams.append("test-environment", environment);
    }

    

    const urlParams = new URLSearchParams(location.search);
    const redirectAfterLoad = urlParams.get('redirectAfterLoad')?.replace("meetingageda", "meetingagenda")
    
    if (redirectAfterLoad && location.pathname === '/my_wsg.asp') {
      let redirectAttempted = false;
      
      const handleIframeLoad = () => {
        if (!redirectAttempted) {
          redirectAttempted = true;
          setTimeout(() => {
            window.location.href = redirectAfterLoad;
          }, 1000);
        }
      };
      
      iframe.onload = () => {
        completeLegacyLoad();
        handleIframeLoad();
      };
    } else {
      iframe.onload = () => {
        completeLegacyLoad();
      };
    }
    
    iframe.onerror = () => {
      completeLegacyLoad();
    };
    
    iframe.src = url.toString();
  };

  useEffect(() => {
    if (!cookieSet || !iframeRef.current) {
      return;
    }
    loadIframe();
  }, [cookieSet, location, reloadSignal, loggedPerson, id, isStaffUrl]);

  

  useEffect(() => {
    window.addEventListener("message", handleIframeEvents);
    return () => {
      window.removeEventListener("message", handleIframeEvents);
    };
  }, [location]);

  return (
    <div ref={containerRef} className="w-full flex flex-col">
      {isLandingPage ? (
        <>
          <div className="w-full" style={{ height: iframeHeight, flexShrink: 0 }}>
            <iframe
              id="legacy-iframe"
              ref={iframeRef}
              className="w-full"
              style={{ height: iframeHeight }}
              title="Legacy Content"
              scrolling="yes"
            />
          </div>
          <div ref={footerRef} className="w-full" style={{ flexShrink: 0 }}>
            <Footer />
          </div>
        </>
      ) : (
        <>
          <div className="w-full" style={{ minHeight: iframeHeight }}>
            <iframe
              id="legacy-iframe"
              ref={iframeRef}
              className="w-full"
              style={{ height: iframeHeight }}
              title="Legacy Content"
            />
          </div>
          <div ref={footerRef} className="w-full mt-auto">
            <Footer />
          </div>
        </>
      )}
    </div>
  );
};

export default LegacyIFrame;
